import { spawn } from "node:child_process";
import { heartbeat, lineStreamer, type Logger } from "./log.js";

export interface AgentRunResult {
  /** The final `result` stream event, or undefined if the agent never emitted one. */
  envelope: unknown;
  /** Raw concatenated stdout (newline-delimited JSON), preserved for debugging. */
  stdout: string;
  stderr: string;
  code: number | null;
  timedOut: boolean;
}

export interface AgentRunOptions {
  executable: string;
  args: string[];
  cwd: string;
  timeoutMs: number;
  /** Short human label for progress lines, e.g. "planning agent". */
  label: string;
  log: Logger;
}

/**
 * Spawn a `claude` agent with `--output-format stream-json` and surface its
 * work as it happens: each assistant message and tool call is logged live, a
 * heartbeat marks long silences, and the terminal `result` event is returned
 * as the envelope for structured-output parsing.
 */
export function runClaudeAgent(options: AgentRunOptions): Promise<AgentRunResult> {
  const { executable, args, cwd, timeoutMs, label, log } = options;
  return new Promise((resolve, reject) => {
    const env = { ...process.env };
    delete env.ANTHROPIC_API_KEY;
    delete env.ANTHROPIC_AUTH_TOKEN;

    const child = spawn(executable, args, { cwd, env, stdio: ["ignore", "pipe", "pipe"] });
    log.debug(`spawned ${executable} (pid ${child.pid ?? "?"})`);
    const stopHeartbeat = heartbeat(log, label);

    let stdout = "";
    let stderr = "";
    let envelope: unknown;
    let timedOut = false;

    const stdoutLines = lineStreamer((line) => {
      let event: unknown;
      try {
        event = JSON.parse(line);
      } catch {
        log.debug(line);
        return;
      }
      const result = logStreamEvent(log, event);
      if (result !== undefined) envelope = result;
    });
    const stderrLines = lineStreamer((line) => log.debug(line));

    child.stdout.setEncoding("utf8");
    child.stderr.setEncoding("utf8");
    child.stdout.on("data", (chunk: string) => { stdout += chunk; stdoutLines.push(chunk); });
    child.stderr.on("data", (chunk: string) => { stderr += chunk; stderrLines.push(chunk); });

    child.once("error", (error) => {
      clearTimeout(timer);
      stopHeartbeat();
      reject(error);
    });

    const timer = setTimeout(() => {
      timedOut = true;
      log.error(`${label} exceeded ${timeoutMs}ms timeout; terminating`);
      child.kill("SIGTERM");
    }, timeoutMs);

    child.once("close", (code) => {
      clearTimeout(timer);
      stopHeartbeat();
      stdoutLines.flush();
      stderrLines.flush();
      resolve({ envelope, stdout, stderr, code, timedOut });
    });
  });
}

/**
 * Translate a single stream-json event into a progress log line. Returns the
 * event itself when it is the terminal `result` envelope, otherwise undefined.
 */
function logStreamEvent(log: Logger, event: unknown): unknown {
  if (typeof event !== "object" || event === null) return undefined;
  const record = event as Record<string, unknown>;

  switch (record.type) {
    case "system":
      if (record.subtype === "init") {
        const tools = Array.isArray(record.tools) ? record.tools.length : 0;
        log.debug(`session started (model ${String(record.model ?? "?")}, ${tools} tools)`);
      }
      return undefined;
    case "assistant":
      logAssistantMessage(log, record.message);
      return undefined;
    case "user":
      log.debug("tool result received");
      return undefined;
    case "result":
      return event;
    default:
      return undefined;
  }
}

function logAssistantMessage(log: Logger, message: unknown): void {
  if (typeof message !== "object" || message === null) return;
  const content = (message as Record<string, unknown>).content;
  if (!Array.isArray(content)) return;

  for (const block of content) {
    if (typeof block !== "object" || block === null) continue;
    const b = block as Record<string, unknown>;
    if (b.type === "text" && typeof b.text === "string" && b.text.trim()) {
      log.info(truncate(b.text.trim(), 2_000));
    } else if (b.type === "tool_use" && typeof b.name === "string") {
      if (b.name === "StructuredOutput") {
        log.debug("producing structured result");
      } else {
        log.info(`→ ${b.name}${formatToolInput(b.input)}`);
      }
    }
  }
}

function formatToolInput(input: unknown): string {
  if (typeof input !== "object" || input === null) return "";
  const record = input as Record<string, unknown>;
  const primary =
    firstString(record.command) ??
    firstString(record.file_path) ??
    firstString(record.pattern) ??
    firstString(record.url) ??
    firstString(record.path);
  if (primary) return `(${truncate(primary.replace(/\s+/g, " "), 120)})`;
  const keys = Object.keys(record);
  return keys.length ? `(${truncate(keys.join(", "), 120)})` : "";
}

function firstString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value : undefined;
}

function truncate(value: string, max: number): string {
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}
